<template>
	<Teleport to="body">
		<component
			v-for="(instance, index) in sortedModalInstances"
			:key="`${instance.name}-${instance.timestamp}`"
			:is="getModalComponent(instance.name)"
			:style="{ '--modal-index': index }"
			v-bind="getModalProps(instance.data)"
			@close="() => handleModalClose(instance.name)"
		/>
	</Teleport>
</template>

<script setup lang="ts">
import { defineAsyncComponent, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useModal } from '@/composables/useModal.ts'
import type { ModalRegistry, ModalData, ModalInstance } from '@/types/modal.ts'
import { router } from 'plugins/router'

const route = useRoute()
const { activeModalInstances, closeModal, openModal } = useModal()

const noteCreateEdit = () => import('components/modules/Notes/CreateEdit.vue')
const taskCreateEdit = () =>
	import('components/modules/Tasks/TaskCreateEdit.vue')
const craftsmanTaskCreateEdit = () =>
	import('components/modules/CraftsmanTasks/CraftsmanTaskCreateEdit.vue')
const cisCreateEdit = () => import('components/modules/Cis/CisCreateEdit.vue')

// Modal component registry with proper typing
const modalRegistry: ModalRegistry = {
	reportView: () =>
		import('@/components/modules/PendingRequests/Reports/Report.vue'),
	reportCreate: () => import('@/components/modules/Reports/CreateReport.vue'),
	cisCreate: cisCreateEdit,
	cisUpdate: cisCreateEdit,
	taskCreate: taskCreateEdit,
	taskUpdate: taskCreateEdit,
	craftsmanTaskUpdate: craftsmanTaskCreateEdit,
	craftsmanTaskCreate: craftsmanTaskCreateEdit,
	noteEdit: noteCreateEdit,
	noteCreate: noteCreateEdit,
	noteView: () => import('components/modules/Notes/View.vue'),
	notificationView: () => import('elements/Notifications/View.vue'),
	notificationDocuments: () => import('elements//Notifications/Documents.vue'),
	sendGreeting: () =>
		import('components/templates/Datatable/Selected/SendGreeting.vue'),
	dialogFilePreview: () =>
		import('@/components/modules/Documents/DialogPreview.vue'),
	buildingPropertyManager: () =>
		import(
			'components/templates/Datatable/Selected/BuildingPropertyManager.vue'
		),
	buildingExport: () =>
		import('components/templates/Datatable/Selected/BuildingExport.vue'),
	documenttypeExport: () =>
		import('components/templates/Datatable/Selected/DocumenttypeExport.vue'),
	documenttypeCutoff: () =>
		import('components/templates/Datatable/Selected/DocumenttypeCutoff.vue'),
	documenttypeBulkEdit: () =>
		import('components/templates/Datatable/Selected/DocumenttypeBulkEdit.vue'),
}

// Sort modals by timestamp to maintain proper z-index order
const sortedModalInstances = computed((): ModalInstance[] => {
	return [...activeModalInstances.value].sort(
		(a, b) => a.timestamp - b.timestamp
	)
})

const getModalComponent = (modalName: string) => {
	const componentLoader = modalRegistry[modalName]
	if (typeof componentLoader === 'undefined') {
		console.error(`Modal component "${modalName}" not found in registry`)
		router.push({ name: 'error', params: { id: 404 } })
		return null
	}

	return defineAsyncComponent({
		loader: componentLoader,
		delay: 100,
		timeout: 5000,
	})
}

const getModalProps = (modalData: ModalData): Record<string, any> => {
	// Remove internal properties before passing to component
	const { _timestamp, ...props } = modalData
	return props
}

const handleModalClose = (modalName: string): void => {
	closeModal(modalName)
}

// Handle route-based modal opening (for direct URL access/refresh)
watch(
	() => route.query,
	(newQuery) => {
		const modalName = newQuery.modal as string

		if (
			modalName &&
			!activeModalInstances.value.find((m) => m.name === modalName)
		) {
			// Parse modal data from query params
			const { modal, ...modalData } = newQuery

			// Convert query params back to proper types
			const parsedData: ModalData = {}
			Object.entries(modalData).forEach(([key, value]) => {
				if (typeof value === 'string') {
					try {
						parsedData[key] = JSON.parse(value)
					} catch {
						if (value === 'true') parsedData[key] = true
						else if (value === 'false') parsedData[key] = false
						else if (!isNaN(Number(value)) && value !== '')
							parsedData[key] = Number(value)
						else parsedData[key] = value
					}
				} else if (Array.isArray(value)) {
					// Handle array query params
					parsedData[key] = value
				} else {
					parsedData[key] = value
				}
			})

			// Open modal without route sync to avoid infinite loop
			openModal(modalName, parsedData, { syncWithRoute: false })
		}
	},
	{ immediate: true, deep: true }
)

// Handle browser back button
watch(
	() => route.query.modal,
	(newModal, oldModal) => {
		if (!newModal && oldModal && typeof oldModal === 'string') {
			// Modal was closed via back button
			closeModal(oldModal, { syncWithRoute: false })
		}
	}
)
</script>

<style scoped></style>
